// Shared chat composer — the multi-line input box used by BOTH the widget and
// the dashboard SPA (à la Claude Code's prompt box: a bordered rounded
// container with the textarea on top and an action bar underneath, send button
// bottom-right). Starts one row tall and auto-grows with the content up to
// `maxRows`, then scrolls. Presentational + mechanism only: Enter submits,
// Shift+Enter inserts a newline, empty input can't send. All copy
// (placeholder, footer) comes from the host. Hosts load the matching styles:
// `@netwatchai/chat-core/composer.css` (the widget inlines it into its shadow
// DOM).
import { useCallback, useLayoutEffect, useRef } from 'react'
import type { FormEvent, KeyboardEvent, ReactNode, Ref } from 'react'

// Must match the textarea's line-height in composer.css — the JS autosize
// measures scrollHeight, but the max-rows cap is this CSS clamp.
const LINE_HEIGHT_EM = 1.45

export interface ComposerProps {
  value: string
  onChange: (value: string) => void
  /** Called on Enter or the send button. Host guards busy/empty itself too. */
  onSubmit: () => void
  placeholder?: string
  /** Disables typing AND sending (e.g. a pending clarify question). */
  disabled?: boolean
  /** A reply is streaming: the send button becomes a stop button (typing stays
   *  enabled; Enter/submit are ignored until the turn ends). */
  busy?: boolean
  /** Called when the user clicks the stop button while busy — interrupt the
   *  stream and give control back. Without it, busy just disables sending. */
  onStop?: () => void
  /** Rows the box may auto-grow to before it scrolls. */
  maxRows?: number
  inputRef?: Ref<HTMLTextAreaElement>
  /** Rendered bottom-left in the action bar, opposite the send button. */
  accessory?: ReactNode
  /** Rendered under the box (e.g. the widget's "Powered by" line). */
  footer?: ReactNode
  /** AI-fallibility line rendered as the LAST row under the box on every
   *  surface. Pass a string to reword it, or `false` to hide. */
  disclaimer?: ReactNode | false
}

const DEFAULT_DISCLAIMER = 'Quark can make mistakes. Please double-check responses.'

function IconSend() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 19V5M6 11l6-6 6 6" />
    </svg>
  )
}

function IconStop() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="3" />
    </svg>
  )
}

export function Composer({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  busy,
  onStop,
  maxRows = 10,
  inputRef,
  accessory,
  footer,
  disclaimer = DEFAULT_DISCLAIMER,
}: ComposerProps) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null)
  // Merge our measuring ref with the host's (the widget focuses via its own).
  const setRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      innerRef.current = el
      if (typeof inputRef === 'function') inputRef(el)
      else if (inputRef) inputRef.current = el
    },
    [inputRef],
  )
  // Autosize: collapse, then grow to fit the content. The CSS max-height (from
  // maxRows) clamps the rendered height and flips the textarea into scrolling.
  useLayoutEffect(() => {
    const el = innerRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [value])
  const submit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      if (busy) return
      onSubmit()
    },
    [onSubmit, busy],
  )
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (busy) return
        onSubmit()
      }
    },
    [onSubmit, busy],
  )
  return (
    <form className="nw-composer" onSubmit={submit}>
      <div className="nw-composer-box">
        <textarea
          ref={setRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={1}
          style={{ maxHeight: `${LINE_HEIGHT_EM * maxRows}em` }}
          disabled={disabled}
        />
        <div className="nw-composer-bar">
          <div className="nw-composer-accessory">{accessory}</div>
          {busy && onStop ? (
            <button
              type="button"
              className="nw-composer-send is-stop"
              onClick={onStop}
              aria-label="Stop response"
            >
              <IconStop />
            </button>
          ) : (
            <button
              type="submit"
              className="nw-composer-send"
              disabled={disabled || busy || !value.trim()}
              aria-label="Send message"
            >
              <IconSend />
            </button>
          )}
        </div>
      </div>
      {footer != null && <div className="nw-composer-footer">{footer}</div>}
      {disclaimer !== false && disclaimer != null && (
        <div className="nw-composer-disclaimer">{disclaimer}</div>
      )}
    </form>
  )
}
